import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";
import { AreaSelectionStore } from "../state/area-selection.store";

/**
 * Guard that checks if the user needs to select an area.
 * 
 * This guard:
 * 1. Checks if area selection has already been shown this session
 * 2. Checks if the user already has a selected area
 * 3. If both conditions are false, stores the original requested URL and redirects to area selection
 * 4. After area selection, the user will be redirected back to their original requested URL
 * 
 * @param _route - The activated route snapshot
 * @param state - The router state snapshot
 * @returns Promise<boolean> - true if user can proceed, false if should be redirected
 */
export async function AreaSelectionGuardFn(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
  const router = inject(Router);
  const areaSelectionStore = inject(AreaSelectionStore);

  // Check if area selection has been shown this session
  const areaSelectionShown = localStorage.getItem("areaSelectionShown") === "true";
  
  // Check if user already has a selected area
  const currentSelectedAreaId = areaSelectionStore.selectedAreaId;

  // If area selection has been shown or user has a selected area, allow navigation
  if (areaSelectionShown || currentSelectedAreaId) {
    return true;
  }

  // If we're already on the area selection page, allow navigation
  if (state.url === "/area-selection") {
    return true;
  }

  // Store the original requested URL before redirecting
  localStorage.setItem("originalRequestedUrl", state.url);
  
  // Redirect to area selection
  await router.navigate(["/area-selection"]);
  return false;
}

/**
 * Guard that prevents access to area selection if it's not needed.
 * 
 * This guard ensures the area selection page is only shown when necessary.
 * It prevents users from returning to area selection via back button or direct navigation
 * once they have already selected an area.
 * 
 * @param _route - The activated route snapshot  
 * @param _state - The router state snapshot
 * @returns Promise<boolean> - true if user should see area selection, false if should be redirected
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function AreaSelectionPageGuardFn(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Promise<boolean> {
  const router = inject(Router);
  const areaSelectionStore = inject(AreaSelectionStore);

  // Check if user already has a selected area
  const currentSelectedAreaId = areaSelectionStore.selectedAreaId;

  // If user has a selected area, they should never see area selection page again
  // Always redirect to homepage regardless of how they got here (back button, direct navigation, etc.)
  if (currentSelectedAreaId !== null) {
    await router.navigate(["/homepage"]);
    return false;
  }

  // Only allow access to area selection page if user has no selected area
  // This ensures users only see it when they genuinely need to select an area
  return true;
}
